import api from "../hooks/api";

/**
 * Multi-tenant backoffice selection (ADR-021).
 *
 * The selected tenant is persisted and forwarded as `X-Selected-Tenant-ID`
 * (see hooks/api.tsx). The api-gw validates it against the caller's
 * server-derived grant and returns 403 if not allowed — the client never
 * authorises itself, it only *expresses* a choice.
 */
const STORAGE_KEY = "selectedTenantId";

export function getSelectedTenant(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function setSelectedTenant(tenantId: string): void {
  if (tenantId) {
    localStorage.setItem(STORAGE_KEY, tenantId);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export interface TenantInfo {
  id: string;
  name: string;
  subdomain?: string;
}

export interface TenantsResponse {
  tenantIds: string[];
  tenants?: TenantInfo[];
}

/** Tenants the logged-in operator may act on (server-derived). */
export async function fetchManageableTenants(): Promise<TenantInfo[]> {
  try {
    // The CDN routes /api/auth-mgmt/* to the api-gw (→ auth-ms /auth/me/tenants).
    // /api/auth/* on the niposom host goes to the niposom backend, so a distinct
    // prefix is required for auth-ms (see infra CDN nginx-config).
    const res = await api.get<TenantsResponse>("/auth-mgmt/me/tenants");
    // Prefer the enriched projection (friendly names); fall back to bare IDs
    // for older auth-ms builds that only return tenantIds.
    if (res.data.tenants?.length) return res.data.tenants;
    return (res.data.tenantIds ?? []).map((id) => ({ id, name: id }));
  } catch {
    // Non-fatal: without the list we simply don't render a selector and keep
    // the single-tenant behaviour (the token's own tenant).
    return [];
  }
}
